import React from 'react';
import { blockHasPlacement, supportsMultiplePlacements, textBlockHasValue, getBlockPlacementEntries } from './utils';

function TextBlockInput({
  block,
  onValueChange,
  onPlace,
  onReset,
  queuePlacementForBlock,
}) {
  const hasPlacement = blockHasPlacement(block);
  const supportsMultiple = supportsMultiplePlacements(block);
  const placementEntries = getBlockPlacementEntries(block);
  const placementCount = placementEntries.length;
  const totalDefaultPlacements = block.totalDefaultPlacements || 0;
  const allPlacementsUsed = totalDefaultPlacements > 0 && placementCount >= totalDefaultPlacements;
  
  // Button should only be enabled if there's a value (required for date fields)
  const hasValue = textBlockHasValue(block);

  const canInitiatePlacement = hasValue && (!hasPlacement || (supportsMultiple && !allPlacementsUsed));

  // Shortened button labels for compact layout
  const getPlaceButtonLabel = () => {
    if (hasPlacement && supportsMultiple) return 'Add';
    if (hasPlacement) return 'Placed';
    return 'Place';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-2.5">
      <div className="flex flex-col 2xl:flex-row 2xl:items-start gap-1.5 2xl:gap-2">
        {/* Label */}
        <label className="text-xs font-semibold text-gray-900 w-full 2xl:w-24 flex-shrink-0 2xl:pt-1.5">
          {block.label}
        </label>

        {/* Input field and indicator container */}
        <div className="flex-1 min-w-0 w-full 2xl:w-auto">
          <div className="flex items-center gap-2">
            {/* Input field */}
            <div className="flex-1 min-w-0">
              {block.type === 'date' ? (
                <input
                  data-tour={block.id === 'sign-date' ? 'signing-date-input' : undefined}
                  type="date"
                  className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={block.value ? new Date(block.value).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const isoValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                    onValueChange(block.id, isoValue);
                    if (isoValue && block.captureValuePerPlacement) {
                      queuePlacementForBlock(block, isoValue);
                    }
                  }}
                  placeholder={block.placeholder}
                />
              ) : (
                <input
                  data-tour={block.id === 'signer-name' ? 'signer-name-input' : undefined}
                  type="text"
                  className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={block.placeholder}
                  value={block.value || ''}
                  onChange={(e) => onValueChange(block.id, e.target.value)}
                />
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-1.5 flex-shrink-0">
              <button
                data-tour={
                  block.id === 'signer-name' ? 'signer-name-place' :
                  block.id === 'sign-date' ? 'signing-date-place' : undefined
                }
                onClick={() => onPlace(block.id)}
                disabled={!canInitiatePlacement}
                className="px-2.5 py-1.5 text-xs font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:text-gray-500 transition-colors flex items-center gap-1"
                title={
                  !hasValue ? `Enter ${block.label.toLowerCase()} first` :
                  allPlacementsUsed ? `All ${totalDefaultPlacements} placements added` :
                  hasPlacement && !supportsMultiple ? 'Already placed - use Undo to remove' :
                  supportsMultiple && totalDefaultPlacements > 0 ? `${placementCount}/${totalDefaultPlacements} placed` :
                  ''
                }
              >
                {hasPlacement && supportsMultiple && (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                )}
                {getPlaceButtonLabel()}
              </button>
              <button
                onClick={() => onReset(block.id)}
                disabled={!hasPlacement}
                className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                title={hasPlacement ? (placementCount > 1 ? `Undo last placement (${placementCount} total)` : 'Undo placement') : ''}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Undo
                {placementCount > 1 && (
                  <span className="ml-0.5 text-[10px] font-semibold text-gray-600">
                    ({placementCount})
                  </span>
                )}
              </button>
            </div>
          </div>
          
          {/* Multiple placements indicator - below input and buttons */}
          {supportsMultiple && totalDefaultPlacements > 1 && (
            <div className="mt-1.5 text-[10px] text-gray-500">
              {allPlacementsUsed 
                ? `All ${totalDefaultPlacements} placements filled`
                : placementCount > 0
                  ? `${placementCount} of ${totalDefaultPlacements} placements filled`
                  : `Can be placed ${totalDefaultPlacements} times`
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TextBlockInput;

